import React, { useState, useRef, useEffect } from 'react';
import { 
  Copy, 
  Check,
  Zap,
  Layers,
  Brain,
  Target,
  Activity,
  Eye,
  ShieldCheck
} from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { codeExamples, outputExamples } from '../data/codeExamples';
import { API_PRICING } from '../config/constants';
import './CodeExamplesContent.css';

interface CodeExamplesContentProps {
  apiKey: string;
  showDocumentation?: boolean;
}

const CodeExamplesContent: React.FC<CodeExamplesContentProps> = ({ apiKey, showDocumentation = false }) => {
  const [selectedApi, setSelectedApi] = useState<'face-detect' | 'face-detect-batch' | 'ai-image-detect' | 'composite-check' | 'trust-score' | 'text-analysis' | 'face-verify' | 'moderation-submit-profile'>('moderation-submit-profile');
  const [selectedLanguage, setSelectedLanguage] = useState<'javascript' | 'python' | 'curl' | 'php' | 'go' | 'dart'>('javascript');
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});
  const animationFrameRef = useRef<number | null>(null);
  const isScrollingRef = useRef<boolean>(false);
  const lastScrollPositionRef = useRef<number>(window.pageYOffset);

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Cancel animation on user scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isScrollingRef.current) {
        const currentScrollPosition = window.pageYOffset;
        const scrollDifference = Math.abs(currentScrollPosition - lastScrollPositionRef.current);
        
        // If user scrolled more than 5px, cancel the animation
        if (scrollDifference > 5) {
          if (animationFrameRef.current !== null) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
          }
          isScrollingRef.current = false;
        }
      }
      lastScrollPositionRef.current = window.pageYOffset;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const scrollToSelectionSection = () => {
    const selectionSection = document.querySelector('.selection-section');
    if (selectionSection) {
      const sectionRect = selectionSection.getBoundingClientRect();
      const navbar = document.querySelector('.navigation');
      const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 80;
      const targetPosition = window.pageYOffset + sectionRect.top - navbarHeight;
      
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      const duration = 1500; // 1.5 seconds for slower, smoother scroll
      let start: number | null = null;

      // Cancel any existing animation
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      isScrollingRef.current = true;
      lastScrollPositionRef.current = startPosition;

      const easeInOutCubic = (t: number): number => {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };

      const animation = (currentTime: number) => {
        if (!isScrollingRef.current) {
          return; // Animation was cancelled
        }

        if (start === null) {
          start = currentTime;
          // Ensure we start from the exact current position on the first frame
          window.scrollTo(0, startPosition);
          animationFrameRef.current = requestAnimationFrame(animation);
          return;
        }
        
        const timeElapsed = currentTime - start;
        const progress = Math.min(timeElapsed / duration, 1);
        const ease = easeInOutCubic(progress);
        
        window.scrollTo(0, startPosition + distance * ease);
        lastScrollPositionRef.current = window.pageYOffset;
        
        if (timeElapsed < duration && isScrollingRef.current) {
          animationFrameRef.current = requestAnimationFrame(animation);
        } else {
          isScrollingRef.current = false;
          animationFrameRef.current = null;
        }
      };

      animationFrameRef.current = requestAnimationFrame(animation);
    }
  };

  const handleApiSelect = (api: 'face-detect' | 'face-detect-batch' | 'ai-image-detect' | 'composite-check' | 'trust-score' | 'text-analysis' | 'face-verify' | 'moderation-submit-profile') => {
    setSelectedApi(api);
    // Only scroll if we're on the landing docs page (showDocumentation is true)
    if (showDocumentation) {
      scrollToSelectionSection();
    }
  };

  const currentCode = (codeExamples[selectedApi] as any)?.[selectedLanguage]?.replace(/YOUR_API_KEY/g, apiKey) || '';
  const currentOutput = (outputExamples[selectedApi] as any)?.[selectedLanguage] || '';
  const copyKey = `${selectedApi}-${selectedLanguage}`;
  const outputCopyKey = `output-${selectedApi}-${selectedLanguage}`;

  return (
    <div className="code-examples-content">
      {/* API Selection */}
      <div className="selection-section">
        {/* <h3 className="section-title">Choose API</h3> */}
        <div className="api-options">
          <button
            onClick={() => handleApiSelect('moderation-submit-profile')}
            className={`api-option ${selectedApi === 'moderation-submit-profile' ? 'active' : ''}`}
          >
            <div className="option-icon">
              <ShieldCheck className="icon" />
            </div>
            <div className="option-content">
              <div className="option-name">Moderation Submit</div>
              <div className="option-description">Submit profile for moderation</div>
              <div className="option-endpoint">/api/moderation/submit-profile</div>
            </div>
          </button>

          <button
            onClick={() => handleApiSelect('face-detect')}
            className={`api-option ${selectedApi === 'face-detect' ? 'active' : ''}`}
          >
            <div className="option-icon">
              <Target className="icon" />
            </div>
            <div className="option-content">
              <div className="option-name">Face Detection</div>
              <div className="option-description">Detect faces in images with detailed analysis</div>
              <div className="option-endpoint">/api/face-detect</div>
            </div>
          </button>

          <button
            onClick={() => handleApiSelect('face-detect-batch')}
            className={`api-option ${selectedApi === 'face-detect-batch' ? 'active' : ''}`}
          >
            <div className="option-icon">
              <Layers className="icon" />
            </div>
            <div className="option-content">
              <div className="option-name">Batch Face Detection</div>
              <div className="option-description">Detect faces in multiple images at once</div>
              <div className="option-endpoint">/api/face-detect-batch</div>
            </div>
          </button>

          <button
            onClick={() => handleApiSelect('ai-image-detect')}
            className={`api-option ${selectedApi === 'ai-image-detect' ? 'active' : ''}`}
          >
            <div className="option-icon">
              <Brain className="icon" />
            </div>
            <div className="option-content">
              <div className="option-name">AI Image Detection</div>
              <div className="option-description">Detect AI-generated images</div>
              <div className="option-endpoint">/api/ai-image-detect</div>
            </div>
          </button>

          <button
            onClick={() => handleApiSelect('composite-check')}
            className={`api-option ${selectedApi === 'composite-check' ? 'active' : ''}`}
          >
            <div className="option-icon">
              <Activity className="icon" />
            </div>
            <div className="option-content">
              <div className="option-name">Composite Check</div>
              <div className="option-description">Combine a combination of checks into one</div>
              <div className="option-endpoint">/api/composite-check</div>
            </div>
          </button>

          <button
            onClick={() => handleApiSelect('text-analysis')}
            className={`api-option ${selectedApi === 'text-analysis' ? 'active' : ''}`}
          >
            <div className="option-icon">
              <Brain className="icon" />
            </div>
            <div className="option-content">
              <div className="option-name">Text Analysis</div>
              <div className="option-description">Analyze bio, prompts, and metadata for authenticity</div>
              <div className="option-endpoint">/api/text-analysis</div>
            </div>
          </button>

          <button
            onClick={() => handleApiSelect('trust-score')}
            className={`api-option ${selectedApi === 'trust-score' ? 'active' : ''}`}
          >
            <div className="option-icon">
              <Zap className="icon" />
            </div>
            <div className="option-content">
              <div className="option-name">Trust Score</div>
              <div className="option-description">Analyze everything in one request</div>
              <div className="option-endpoint">/api/trust-score</div>
            </div>
          </button>

          <button
            onClick={() => handleApiSelect('face-verify')}
            className={`api-option ${selectedApi === 'face-verify' ? 'active' : ''}`}
          >
            <div className="option-icon">
              <Eye className="icon" />
            </div>
            <div className="option-content">
              <div className="option-name">Face Verify</div>
              <div className="option-description">Verify if selfie matches any profile images</div>
              <div className="option-endpoint">/api/face-verify</div>
            </div>
          </button>

        </div>
      </div>

      {/* API Documentation - Only show in DocsPage */}
      {showDocumentation && (
        <div className="api-documentation">
          <h3 className="section-title">About This API</h3>
          <div className="documentation-content">
            {selectedApi === 'face-detect' && (
              <>
                <p>
                  <strong>Face Detection API</strong> (Side Product) analyzes single images to identify and locate human faces. 
                  This endpoint is available as a standalone API for applications that need only face detection functionality.
                </p>
                 <p>
                   <strong>When to use:</strong> Use this API when you need only face detection in individual images and don't need the full moderation workflow.
                   For most use cases, we recommend using the <strong>Submit Profile API</strong> instead, which includes face detection along with comprehensive moderation features.
                 </p>
                 <p className="cost-section">
                   <strong>Cost:</strong> ${API_PRICING['/api/face-detect']} per request
                 </p>
               </>
             )}

            {selectedApi === 'face-detect-batch' && (
              <>
                <p>
                  <strong>Batch Face Detection API</strong> (Side Product) processes multiple images simultaneously for efficient 
                  face detection. This endpoint is available as a standalone API for batch face detection needs.
                </p>
                 <p>
                   <strong>When to use:</strong> Use this API when you need only batch face detection and don't need the full moderation workflow.
                   For most use cases, we recommend using the <strong>Submit Profile API</strong> instead, which handles multiple images along with comprehensive moderation features.
                 </p>
                 <p className="cost-section">
                   <strong>Cost:</strong> ${API_PRICING['/api/face-detect-batch']} per request
                 </p>
               </>
             )}

            {selectedApi === 'ai-image-detect' && (
              <>
                <p>
                  <strong>AI Image Detection API</strong> (Side Product) identifies whether images were generated by AI
                  (like DALL-E, Midjourney, or Stable Diffusion). This endpoint is available as a standalone API for AI detection needs.
                </p>
                 <p>
                   <strong>When to use:</strong> Use this API when you need only AI image detection and don't need the full moderation workflow.
                   For most use cases, we recommend using the <strong>Submit Profile API</strong> instead, which includes AI detection along with comprehensive moderation features.
                 </p>
                 <p className="cost-section">
                   <strong>Cost:</strong> ${API_PRICING['/api/ai-image-detect']} per request
                 </p>
               </>
             )}

            {selectedApi === 'composite-check' && (
              <>
                <p>
                  <strong>Composite Check API</strong> combines face detection and AI image detection in a single 
                  request. This powerful endpoint provides comprehensive analysis by running multiple checks 
                  simultaneously on the same image.
                </p>
                 <p>
                   <strong>When to use:</strong> Use this API when you need both face detection and AI content 
                   analysis for the same image. Perfect for dating apps profile setup workflow, where the a certain profile image uploaded by the user is checked for a face and then checked for whether it was AI-generated. This is the most latency-efficient endpoint for performing both checks on an image.
                 </p>
                 <p className="cost-section">
                   <strong>Cost:</strong> ${API_PRICING['/api/composite-check']} per request (when both checks are selected)
                 </p>
               </>
             )}

            {selectedApi === 'trust-score' && (
              <>
                <p>
                  <strong>Trust Score API</strong> (Side Product) combines image analysis and text analysis to generate a unified trust score. 
                  It processes multiple images with different configurations for each image, while also analyzing user bio, prompts, and metadata. 
                  The final trust score is calculated by combining text analysis results with image verification results, providing a holistic assessment of user authenticity.
                </p>
                 <p>
                   <strong>When to use:</strong> Use this API when you need only a trust score calculation and don't need the full moderation workflow with automatic status assignment.
                   For most use cases, we recommend using the <strong>Submit Profile API</strong> instead, which includes trust score calculation along with automatic status assignment, profile storage, and webhook support.
                 </p>

                 <h4 style={{ color: '#ffffff', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Images Field</h4>
                 <p>
                   You can add up to <strong>10 images</strong> in a list format in the <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>images</code> field. 
                   Each image must be provided as a <strong>base64-encoded string</strong>. You can include the data URI prefix (<code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>data:image/jpeg;base64,</code>) or provide just the base64 string. 
                   The images are processed in the order they appear in the list.
                 </p>
                 <p>
                   <strong>Example:</strong>
                 </p>
                 <pre style={{ background: '#0a0a0a', padding: '1rem', borderRadius: '6px', border: '1px solid #333333', overflowX: 'auto', fontSize: '0.875rem', color: '#cccccc' }}>
{`{
  "images": [
    "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQ...",
    "iVBORw0KGgoAAAANSUhEUgAA...",
    "/9j/4AAQSkZJRgABAQAAAQ..."
  ]
}`}
                 </pre>

                 <h4 style={{ color: '#ffffff', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Global Config</h4>
                 <p>
                   The <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>global_configuration</code> field allows you to set default detection settings for all images. 
                   By default, both <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>face_detect</code> and <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>ai_image_detect</code> are set to <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>true</code>.
                 </p>
                 <p>
                   <strong>Example:</strong>
                 </p>
                 <pre style={{ background: '#0a0a0a', padding: '1rem', borderRadius: '6px', border: '1px solid #333333', overflowX: 'auto', fontSize: '0.875rem', color: '#cccccc' }}>
{`{
  "images": [...],
  "global_configuration": {
    "face_detect": true,      // Default: true
    "ai_image_detect": true   // Default: true
  }
}`}
                 </pre>

                 <h4 style={{ color: '#ffffff', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Per-Image Config</h4>
                 <p>
                   The <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>per_image_configuration</code> field allows you to override the global config for specific images. 
                   This is an <strong>object</strong> where each key is a string number (e.g., <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>"0"</code>, <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>"1"</code>) corresponding to the image index in the <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>images</code> array. 
                   If you don't specify a config for a particular image, it will use the global config settings.
                 </p>
                 <p>
                   <strong>Example:</strong> In this example, the first image (index 0) will only run face detection, the fourth image (index 3) will have both checks disabled, and the other images will use the global config defaults.
                 </p>
                 <pre style={{ background: '#0a0a0a', padding: '1rem', borderRadius: '6px', border: '1px solid #333333', overflowX: 'auto', fontSize: '0.875rem', color: '#cccccc' }}>
{`{
  "images": ["image1", "image2", "image3", "image4"],
  "global_configuration": {
    "face_detect": true,
    "ai_image_detect": true
  },
  "per_image_configuration": {
    "0": {
      "ai_image_detect": false  // Override: only check for face on first image and ignore AI generated checks
    },
    "3": {
      "face_detect": false,
      "ai_image_detect": false  // Override: disable both checks on fourth image
    }
    // image2 and image3 will use global_configuration defaults
  }
}`}
                 </pre>

                 <h4 style={{ color: '#ffffff', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Required Fields</h4>
                 <p>
                   All fields are <strong>optional</strong> except for the <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>images</code> field. 
                   You must provide at least <strong>one image</strong> in the images array. All other fields including <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>global_configuration</code>, 
                   <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>per_image_configuration</code>, <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>bio</code>, 
                   <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>prompts</code>, 
                   <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>metadata</code>, and <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>image_block</code> are optional.
                 </p>

                 <h4 style={{ color: '#ffffff', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Bio</h4>
                 <p>
                   The <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>bio</code> field accepts a string containing the user's profile bio or description. 
                   This text is analyzed by the language model along with prompts and metadata to assess the authenticity and trustworthiness of the user profile. 
                   The bio is combined with image verification results to generate the final trust score.
                 </p>
                 <p>
                   <strong>Example:</strong>
                 </p>
                 <pre style={{ background: '#0a0a0a', padding: '1rem', borderRadius: '6px', border: '1px solid #333333', overflowX: 'auto', fontSize: '0.875rem', color: '#cccccc' }}>
{`{
  "images": [...],
  "bio": "Love hiking, photography, and trying new restaurants. Looking for someone who shares my passion for adventure and good food!"
}`}
                 </pre>

                 <h4 style={{ color: '#ffffff', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Prompts</h4>
                 <p>
                   There is <strong>no limit</strong> to the number of prompts you can add. The <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>prompts</code> field accepts an array of objects, 
                   each containing a <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>question</code> and <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>answer</code> field. 
                   You can include as many prompts as needed. These prompts are analyzed by the language model along with the images, bio, and metadata to generate a comprehensive trust score.
                 </p>
                 <p>
                   <strong>Example:</strong>
                 </p>
                 <pre style={{ background: '#0a0a0a', padding: '1rem', borderRadius: '6px', border: '1px solid #333333', overflowX: 'auto', fontSize: '0.875rem', color: '#cccccc' }}>
{`{
  "images": [...],
  "prompts": [
    {
      "question": "What is your favorite hobby?",
      "answer": "I love hiking and photography. I spend most weekends exploring trails and capturing nature."
    },
    {
      "question": "Tell me about your ideal weekend",
      "answer": "A perfect weekend would be a morning hike followed by trying a new restaurant, then relaxing with a good book."
    },
    {
      "question": "Describe your perfect date",
      "answer": "Something active and fun, like a walk in the park or visiting a museum together."
    }
    // Add as many prompts as you need
  ]
}`}
                 </pre>

                 <h4 style={{ color: '#ffffff', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Metadata</h4>
                 <p>
                   The <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>metadata</code> field is a text dump where you can provide additional context about the user or images. 
                   Our language model analyzes this metadata in tandem with the images, bio, and prompts to generate a more comprehensive trust score. 
                   This can include user profile information, account details, or any other relevant textual data.
                 </p>
                 <p>
                   <strong>Example:</strong>
                 </p>
                 <pre style={{ background: '#0a0a0a', padding: '1rem', borderRadius: '6px', border: '1px solid #333333', overflowX: 'auto', fontSize: '0.875rem', color: '#cccccc' }}>
{`{
  "images": [...],
  "metadata": "IP: 193.22.3.235. Device location: London, UK. User joined platform: 2024-01-15. Account verified: Yes. Profile completion: 85%. Last login: 2024-03-20. Reported by users: 0 times."
}`}
                 </pre>

                 <h4 style={{ color: '#ffffff', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Image Block</h4>
                 <p>
                   The <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>image_block</code> field is a boolean that, when set to <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>true</code>, 
                   will cause the trust score to output <strong>0</strong> if any image fails its configured checks. This is useful for strict moderation scenarios where you need to ensure all images pass all checks.
                 </p>
                 <p>
                   <strong>Use Case:</strong> In a dating app, if you require all profile images to contain a face and not be AI-generated, you would set <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>image_block: true</code>. 
                   If any image fails the face detection or AI detection check, the entire trust score will be 0, indicating that the user profile should not be approved.
                 </p>
                 <p>
                   <strong>Example:</strong>
                 </p>
                 <pre style={{ background: '#0a0a0a', padding: '1rem', borderRadius: '6px', border: '1px solid #333333', overflowX: 'auto', fontSize: '0.875rem', color: '#cccccc' }}>
{`{
  "images": ["image1", "image2", "image3"],
  "global_configuration": {
    "face_detect": true,
    "ai_image_detect": true
  },
  "image_block": true  // If any image fails, score = 0
}`}
                 </pre>
                 <p>
                   In this example, if <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>image_block</code> is <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>true</code> and any of the three images fail 
                   (e.g., image2 doesn't contain a face or image3 is AI-generated), the entire trust score will be 0, regardless of the other images passing their checks.
                 </p>

                 <p className="cost-section" style={{ marginTop: '1.5rem' }}>
                   <strong>Cost:</strong> ${API_PRICING['/api/trust-score']} per request (fixed price regardless of configuration)
                 </p>
               </>
             )}

            {selectedApi === 'text-analysis' && (
              <>
                <p>
                  <strong>Text Analysis API</strong> (Side Product) analyzes user profile text content including bio, prompts, 
                  and metadata using AI to detect authenticity and trustworthiness. This endpoint is available as a standalone API for text analysis needs.
                </p>
                 <p>
                   <strong>When to use:</strong> Use this API when you need only text analysis and don't need the full moderation workflow.
                   For most use cases, we recommend using the <strong>Submit Profile API</strong> instead, which includes text analysis along with comprehensive moderation features.
                 </p>
                 <p className="cost-section">
                   <strong>Cost:</strong> ${API_PRICING['/api/text-analysis']} per request
                 </p>
               </>
             )}

            {selectedApi === 'face-verify' && (
              <>
                <p>
                  <strong>Face Verify API</strong> (Side Product) compares a selfie image against a list of profile images to verify 
                  if the selfie matches any of the profile images. This endpoint is available as a standalone API for face verification needs.
                </p>
                 <p>
                   <strong>When to use:</strong> Use this API when you need only face verification and don't need the full moderation workflow.
                   For most use cases, we recommend using the <strong>Submit Profile API</strong> instead, which includes comprehensive profile analysis along with moderation features.
                 </p>
                 <p>
                   <strong>Request format:</strong> Provide a single selfie image and an array of up to 10 profile images. 
                   The API will compare the selfie against each profile image and return all matches that meet the similarity threshold (default: 90).
                 </p>
                 <p>
                   <strong>Response:</strong> Returns a boolean <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>verified</code> field indicating if any matches were found, 
                   an array of all <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>matches</code> with similarity scores and confidence levels, 
                   and a <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>best_match</code> object containing the highest similarity match.
                 </p>
                 <p className="cost-section">
                   <strong>Cost:</strong> ${API_PRICING['/api/face-verify']} per request
                 </p>
               </>
             )}

            {selectedApi === 'moderation-submit-profile' && (
              <>
                <p>
                  <strong>Submit Profile API</strong> submits user profiles for moderation with automatic status assignment based on configured thresholds. 
                  It combines image analysis (face detection, AI image detection) and text analysis (bio, prompts, metadata) to calculate a trust score, 
                  then automatically assigns a status (approved, rejected, or pending) based on your configured moderation thresholds.
                </p>
                 <p>
                   <strong>When to use:</strong> Use this API when you want to submit profiles for moderation and have them automatically assigned a status based on trust scores. 
                   Perfect for dating apps and social platforms that want to automatically approve high-trust profiles, reject low-trust profiles, 
                   and flag medium-trust profiles for manual review. The API uses your configured moderation settings (thresholds and image settings) from the moderation dashboard.
                 </p>
                 <p>
                   <strong>Key differences from Trust Score API:</strong>
                 </p>
                 <ul style={{ color: '#cccccc', marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                   <li>Uses <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>X-API-Key</code> header instead of <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>Authorization: Bearer</code></li>
                   <li>No configuration fields needed - all image processing settings are managed in the Moderation Dashboard</li>
                   <li>Automatically assigns status (approved/rejected/pending) based on configured thresholds</li>
                   <li>Stores profiles in moderation system for review in the dashboard</li>
                   <li>Uses saved image settings from moderation dashboard automatically</li>
                   <li>Can trigger webhooks if configured</li>
                 </ul>

                 <h4 style={{ color: '#ffffff', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Request Format</h4>
                 <p>
                   The API accepts profile information only. All image processing configuration is managed in the Moderation Dashboard:
                 </p>
                 <ul style={{ color: '#cccccc', marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                   <li><code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>images</code> (required) - Array of base64-encoded images (1-10 images)</li>
                   <li><code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>user_id</code> (optional) - User identifier</li>
                   <li><code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>bio</code> (optional) - User bio text</li>
                   <li><code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>prompts</code> (optional) - Array of question/answer pairs</li>
                   <li><code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>metadata</code> (optional) - Additional metadata object</li>
                 </ul>
                 <p style={{ marginTop: '1rem', color: '#888888', fontSize: '0.875rem' }}>
                   <strong>Note:</strong> Image processing settings (face detection, AI image detection, per-image configuration) are configured in the Moderation Dashboard and applied automatically. 
                   You don't need to include <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>global_configuration</code> or <code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>per_image_configuration</code> in your requests.
                 </p>

                 <h4 style={{ color: '#ffffff', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Response Format</h4>
                 <p>
                   The API returns:
                 </p>
                 <ul style={{ color: '#cccccc', marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                   <li><code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>success</code> - Boolean indicating if the request was successful</li>
                   <li><code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>profile_id</code> - Unique identifier for the submitted profile</li>
                   <li><code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>status</code> - Automatically assigned status: "approved", "rejected", or "pending"</li>
                   <li><code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>trust_score</code> - Calculated trust score (0-100)</li>
                   <li><code style={{ background: '#0a0a0a', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#8b5cf6' }}>message</code> - Success message</li>
                 </ul>

                 <h4 style={{ color: '#ffffff', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Automatic Status Assignment</h4>
                 <p>
                   The API automatically assigns status based on your configured moderation thresholds:
                 </p>
                 <ul style={{ color: '#cccccc', marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                   <li><strong>Approved:</strong> Trust score ≥ auto_approve_threshold (default: 70)</li>
                   <li><strong>Rejected:</strong> Trust score &lt; auto_reject_threshold (default: 30)</li>
                   <li><strong>Pending:</strong> Trust score between auto_reject_threshold and auto_approve_threshold</li>
                 </ul>
                 <p>
                   You can configure these thresholds in the Moderation Dashboard under the "Thresholds" button.
                 </p>

                 <h4 style={{ color: '#ffffff', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Image Settings</h4>
                 <p>
                   All image processing settings are configured in the Moderation Dashboard. The API automatically uses your saved settings for:
                 </p>
                 <ul style={{ color: '#cccccc', marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                   <li>Global configuration (face detection, AI image detection defaults)</li>
                   <li>Per-image configuration (overrides for specific image positions)</li>
                   <li>Image block mode</li>
                 </ul>
                 <p>
                   Configure these settings in the Moderation Dashboard under the "Image Settings" button. You don't need to include any configuration in your API requests.
                 </p>

                 <h4 style={{ color: '#ffffff', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Webhooks</h4>
                 <p>
                   If you've configured a webhook URL in the Moderation Dashboard, the API will automatically send webhook notifications when profiles are submitted. 
                   The webhook payload includes the profile ID, status, trust score, and analysis results.
                 </p>

                 <p className="cost-section" style={{ marginTop: '1.5rem' }}>
                   <strong>Cost:</strong> ${API_PRICING['/api/moderation/submit-profile']} per request (fixed price regardless of configuration)
                 </p>
               </>
             )}
          </div>
        </div>
      )}

      {/* Language Selection */}
      <div className="selection-section">
        <h3 className="section-title">Choose Language</h3>
        <div className="language-tabs">
          {['javascript', 'python', 'curl', 'php', 'go', 'dart'].map((lang) => (
            <button
              key={lang}
              onClick={() => setSelectedLanguage(lang as any)}
              className={`language-tab ${selectedLanguage === lang ? 'active' : ''}`}
            >
              {lang.charAt(0).toUpperCase() + lang.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Code Example */}
      <div className="code-section">
        <h3 className="section-title">Code Example</h3>
        <div className="code-block">
          <button
            className="copy-button"
            onClick={() => copyToClipboard(currentCode, copyKey)}
          >
            {copiedStates[copyKey] ? (
              <>
                <Check size={16} />
                Copied!
              </>
            ) : (
              <>
                <Copy size={16} />
                Copy
              </>
            )}
          </button>
          <SyntaxHighlighter
            language={selectedLanguage === 'curl' ? 'bash' : selectedLanguage}
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              background: 'transparent',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontSize: '0.875rem',
              lineHeight: '1.5'
            }}
            showLineNumbers={false}
            wrapLines={true}
            wrapLongLines={true}
          >
            {currentCode}
          </SyntaxHighlighter>
        </div>
      </div>

      {/* Sample Output */}
      <div className="code-section">
        <h3 className="section-title">Sample Output</h3>
        <div className="code-block">
          <button
            className="copy-button"
            onClick={() => copyToClipboard(currentOutput, outputCopyKey)}
          >
            {copiedStates[outputCopyKey] ? (
              <>
                <Check size={16} />
                Copied!
              </>
            ) : (
              <>
                <Copy size={16} />
                Copy
              </>
            )}
          </button>
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              background: 'transparent',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontSize: '0.875rem',
              lineHeight: '1.5'
            }}
            showLineNumbers={false}
            wrapLines={true}
            wrapLongLines={true}
          >
            {currentOutput}
          </SyntaxHighlighter>
        </div>
      </div>
    </div>
  );
};

export default CodeExamplesContent;
